/*eslint react-hooks/exhaustive-deps: "off"*/
/*eslint no-unused-vars: "off"*/
import React, {useEffect, useState} from 'react';
import MapBase from "./MapBase";
import {PALETTE_YlOrRd_9, TRANSPARENT_COLOR} from "../../utils/Palette";
import useMapProps from "./useMapProps";
import {MapPropsProvider} from "./MapPropsContext";
import WidgetContainer from "./widgets/WidgetContainer";
import {arrayToRgb, getApiUrl, getLastYear, numberWithCommas, getGrad, makeTableSorterFn} from "../../utils";
import RMateChartResourceUserStats from "./r_mate_chart/RMateChartResourceUserStats";
import WidgetLegend from "./widgets/WidgetLegend";
import WidgetSelectYear from "./widgets/WidgetSelectYear";
import WidgetDistrictSelect from "./widgets/WidgetDistrictSelect";
import WidgetToolbar from "./widgets/WidgetToolbar";
import WidgetRisingTabs from "./widgets/WidgetRisingTabs";
import WidgetStatisticsTable from "./widgets/WidgetStatisticsTable";
import AlignRightText from "./widgets/AlignRightText";


function MapResourceUserStats() {
  const [fillColorLegend, setFillColorLegend] = useState([]);
  const [searchYear, setSearchYear] = useState("");

  const mapProps = useMapProps({
    INITIAL_GEOJSON_EXTRA_PARAMS: {
      year: getLastYear(),
      month: 12,
      currentDistrictCode: '',
    },
    INITIAL_GEOJSON_POLYGON_LAYER_PROPS: {
      getHoverHtml: (feature, mapDistrictStats) => {
        const [metricObject] = mapDistrictStats;
        const {properties: {pk, name}} = feature;
        const code = pk;
        const obj = metricObject[code];
        if (obj) {
          const {
            total,
            data_type1,
            data_type2,
            data_type3,
            data_type4,
            data_type5,
            data_type6
          } = obj;

          return `<span class='fontStyle'>${name}</span><br/> 
                시가화지수 : ${numberWithCommas(data_type1)}<br/>
                인구 밀도 : ${numberWithCommas(data_type2)}<br/>
                재정자주도 : ${numberWithCommas(data_type3)}<br/>
                정보통신 자격현황 : ${numberWithCommas(data_type4)}<br/>
                전파Tech지수 : ${numberWithCommas(data_type5)}<br/>
                무선국 밀도 : ${numberWithCommas(data_type6)}<br/>
                합계등급 : ${numberWithCommas(total)}<br/>`;
        }
        // metricObject에 없더라도 호버를 띄워줍니다.
        return `<span class='fontStyle'>${name}</span><br/> 
                시가화지수 : 0<br/>
                인구 밀도 : 0<br/>
                재정자주도 : 0<br/>
                정보통신 자격현황 : 0<br/>
                전파Tech지수 : 0<br/>
                무선국 밀도 : 0<br/>
                합계등급 : 0<br/>`;
      },
      getLevelFillColor: ({properties: {pk}}, mapDistrictStats) => {
        const [metricObject, metricMax] = mapDistrictStats;
        const code = pk;
        const obj = metricObject[code];
        // 데이터가 없더라도 색을 입힙니다.
        if (obj) {
          const {total} = (metricObject[code] || {total: 1});
          if (total >= 1) {
            const index = Math.floor(PALETTE_YlOrRd_9.length * (total - 1) / metricMax);
            return PALETTE_YlOrRd_9[index];
          }
          return TRANSPARENT_COLOR;
        }
        return TRANSPARENT_COLOR;
      }
    }
  });

  const {
    currentDistrictCode,
    widgetState,
    dataResponseList,
    mapDistrictStats, setMapDistrictStats, isChangedMapDistrictStats,
    clickedPolygonGeojsonObjects,
    setChartAjaxParams,
    geojsonExtraParams,
    setGeojsonExtraParams,
    setCurrentDistrictCode,
    setTableAjaxParams,
  } = mapProps;


  useEffect(() => {
    const firstDataLayer = dataResponseList.length > 0
      ? dataResponseList[0]
      : null;

    if ( firstDataLayer ) {
      const metricObject = Object
        .entries(firstDataLayer)
        .reduce((acc, [district_code, stats]) => {
          const { TYPE1=0, TYPE2=0, TYPE3=0, TYPE4=0, TYPE5=0, TYPE6=0 } = stats;
          const total = TYPE1 + TYPE2 + TYPE3 + TYPE4 + TYPE5 + TYPE6;
          acc[district_code] = {
            total,
            data_type1: TYPE1,
            data_type2: TYPE2,
            data_type3: TYPE3,
            data_type4: TYPE4,
            data_type5: TYPE5,
            data_type6: TYPE6,
          };
          return acc;
        }, {});


      const metricMax = Math.max(...Object.values(metricObject).map(obj => obj.total));

      const newMapDistrictStats = [metricObject, metricMax];
      if ( isChangedMapDistrictStats(newMapDistrictStats) ) {
        setMapDistrictStats(newMapDistrictStats);

        // 서버통신을 위한 변수 설정
        const { year } = geojsonExtraParams;
        setSearchYear(year);

        const sidoCode = (currentDistrictCode || '').length < 2
          ? ''
          : currentDistrictCode.substr(0, 2);

        setChartAjaxParams({
          sidoCode,
          searchYear : year,
          searchEnd: window.searchYear,
        });
      }
    }
  }, [currentDistrictCode, dataResponseList, widgetState, geojsonExtraParams]);

  useEffect(() => {
    setGeojsonExtraParams(prev => ({
      ...prev,
      currentDistrictCode,
    }));
  }, [currentDistrictCode]);

  useEffect(() => {
    if (clickedPolygonGeojsonObjects.length > 0) {
      const firstLayer = clickedPolygonGeojsonObjects[0];
      const {properties: {name, pk}} = firstLayer;
      setCurrentDistrictCode(pk);
    }
  }, [clickedPolygonGeojsonObjects]);

  useEffect(() => {
    // eslint-disable-next-line no-unused-vars
    const [metricObject, metricMax] = mapDistrictStats;
    if (window.isFinite(metricMax) && metricMax >= 9) {
      setFillColorLegend(
        [...Array(9).keys()].map(colorIndex => {
          const boundary = (metricMax / 9) * (colorIndex + 1);
          const index = Math.floor(PALETTE_YlOrRd_9.length * (boundary - 1) / metricMax);
          const colorRgb = arrayToRgb(PALETTE_YlOrRd_9[index]);
          return [colorRgb, boundary];
        }).reverse()
      );
    }
  }, [mapDistrictStats]);

  useEffect(() => {
    setTableAjaxParams({
      sidoCode: (currentDistrictCode || ''),
      searchEnd: searchYear || getLastYear(),
    });

    setChartAjaxParams({
      sidoCode : (currentDistrictCode || ''),
      searchYear : searchYear || getLastYear(),
      searchEnd: searchYear || getLastYear(),
    });
  }, [currentDistrictCode, searchYear]);

  return (
    <MapPropsProvider value={mapProps}>
      <MapBase isHideWidgetToggleClustering={true} isHideWidgetMapLayerTypes={true}>
        <WidgetContainer>
          <div>
            <WidgetDistrictSelect isShowDistrictLayerFromGeoserver={true} onlyLv1={false}/>
            <WidgetSelectYear/>
          </div>
        </WidgetContainer>
      </MapBase>
      <WidgetRisingTabs titleOnInit={["전파자원 이용자 지수 목록보기"]}>
        <WidgetStatisticsTable
          fetchUrl={getApiUrl({ type: "type1" })}
          type={"type1"}
          getTitle={(layer) => `전파자원 이용자 지수 목록보기`}
          isResourceUserStats={true}
          processor={objectList => {
            const dataCommentList = Array.from(new Set(objectList.map(({data_comment}) => data_comment)));
            const gugunNameList = Array.from(new Set(objectList.map(({gubun_nm}) => gubun_nm)));

            const rowList = gugunNameList.map(ref_gugun_name => {
              return objectList
                .filter(({gubun_nm}) => gubun_nm === ref_gugun_name)
                .reduce((acc, {data_comment, rt, max_rt}) => {
                  acc[data_comment+'수치'] = rt;
                  acc[data_comment+'등급'] = getGrad(max_rt, rt);
                  return acc;
                }, {gubun_nm: ref_gugun_name});
            });

            const columns = dataCommentList.map(comment => ({
              title: comment,
              children: [
                {
                  title: '수치',
                  dataIndex: comment+'수치',
                  key: comment+'수치',
                  render: value => AlignRightText({ text: value, numberComma: true })
                },
                {
                  title: '등급',
                  dataIndex: comment+'등급',
                  key: comment+'등급',
                  render: value => AlignRightText({ text: value, numberComma: false })
                }
              ],
              render: value => value && <div style={{textAlign: 'right'}}>{numberWithCommas(value)}</div>,
            }));

            return [columns, rowList];
          }}
          columns={[
            {title: '순번', dataIndex: 'index', key: 'index', width: 75, fixed: 'left', sorter: makeTableSorterFn('index')},
            {title: '구군', dataIndex: 'gubun_nm', key: 'gubun_nm', width: 120},
          ]}
        />
      </WidgetRisingTabs>

      <RMateChartResourceUserStats/>
      { fillColorLegend.length > 0 && <WidgetLegend fillColorLegend={fillColorLegend}  titleColorLegend={'합계 등급'}  unitColorLegend={''}/>}
      <WidgetToolbar />
    </MapPropsProvider>
  );
}

export default MapResourceUserStats;
